/**
 * Client-side Activity → PDF (jsPDF), aligned with EmergencyLanding export style.
 * Images are downscaled in canvas for a reasonable file size.
 */

import { jsPDF } from 'jspdf';
import { loadLogoDataUrlForPdf } from './pdfCompanyLogo';
import {
  fitImageDrawSize,
  PDF_PRIMARY_PHOTO_BOX,
  PDF_INLINE_PHOTO_BOX,
} from './pdfImageFit';

const PT_H = 842;
const MARGIN = 40;
const CONTENT_W = 515;
const MAX_IMG_W_PT = 500;
const BOTTOM = 56;
const GRID_COL_W = (CONTENT_W - 8) / 2;
const GRID_IMG_BOX = { maxW: GRID_COL_W, maxH: 200 };

/**
 * @param {string} url
 * @returns {Promise<{ dataUrl: string; wPt: number; hPt: number } | null>}
 */
async function loadImageForPdf(url) {
  if (!url || typeof url !== 'string') return null;
  return new Promise((resolve) => {
    const go = (src, cleanup) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        cleanup?.();
        const canvas = document.createElement('canvas');
        const maxPx = 1200;
        let w = img.naturalWidth || img.width;
        let h = img.naturalHeight || img.height;
        if (!w || !h) { resolve(null); return; }
        const scale = Math.min(1, maxPx / Math.max(w, h));
        w = Math.round(w * scale);
        h = Math.round(h * scale);
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        try { ctx.drawImage(img, 0, 0, w, h); } catch { resolve(null); return; }
        let dataUrl;
        try { dataUrl = canvas.toDataURL('image/jpeg', 0.88); } catch { resolve(null); return; }
        const wPt = Math.min(MAX_IMG_W_PT, w * 0.75);
        const hPt = (h / w) * wPt;
        resolve({ dataUrl, wPt, hPt });
      };
      img.onerror = () => { cleanup?.(); resolve(null); };
      img.src = src;
    };

    if (url.startsWith('data:')) { go(url, null); return; }
    fetch(url, { mode: 'cors' })
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        go(objectUrl, () => URL.revokeObjectURL(objectUrl));
      })
      .catch(() => go(url, null));
  });
}

function stampFooter(pdf, n, projectName, { startPage = 1, pageOffset = 0 } = {}) {
  for (let i = startPage; i <= n; i += 1) {
    pdf.setPage(i);
    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.4);
    pdf.line(MARGIN, PT_H - 38, MARGIN + CONTENT_W, PT_H - 38);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(160, 160, 160);
    pdf.text(projectName || 'CrewChain', MARGIN, PT_H - 26);
    const displayPage = i - pageOffset;
    const totalPages = n - pageOffset;
    pdf.text(`Page ${displayPage} of ${totalPages}`, MARGIN + CONTENT_W, PT_H - 26, { align: 'right' });
    pdf.setLineWidth(0.2);
  }
}

/**
 * @param {object} options
 * @param {string} options.projectName
 * @param {string} [options.filterSummary]
 * @param {Array<{
 *   teamName: string,
 *   authorName: string,
 *   timeLabel: string,
 *   body: string,
 *   imageUrls: string[],
 *   voiceLine: string | null,
 *   videoLine: string | null,
 *   thread: Array<{ depth: number, name: string, timeLabel: string, text: string, imageUrls: string[], voiceLine: string | null }>
 * }>} options.posts oldest-first
 * @param {string} [options.companyLogoUrl] — optional; top-right on page 1 when set
 * @param {object} [options.contentOptions] — what to render (defaults = all on)
 */
export async function buildActivityExportPdf({
  projectName,
  filterSummary,
  posts,
  companyLogoUrl = null,
  contentOptions = {},
}) {
  const co = {
    includePostDateTime: true,
    includeTeamName: true,
    includeAuthorName: true,
    includePostBody: true,
    includeMediaNotes: true,
    includeCommentThread: true,
    includeCommentAuthorNames: true,
    includeCommentImages: true,
    ...contentOptions,
  };

  const logo = companyLogoUrl ? await loadLogoDataUrlForPdf(companyLogoUrl) : null;

  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageH = PT_H;
  let y = MARGIN;

  const addPage = () => { pdf.addPage(); y = MARGIN; };
  const addPageIfNeeded = (needPt) => { if (y + needPt > pageH - BOTTOM) addPage(); };

  const gen = new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

  // ── Cover page ──────────────────────────────────────────────────────────────
  if (logo) {
    const logoX = 595 - MARGIN - logo.wPt;
    try { pdf.addImage(logo.dataUrl, logo.format, logoX, 20, logo.wPt, logo.hPt, undefined, 'FAST'); } catch { /* */ }
  }

  let coverY = logo ? Math.max(140, 28 + logo.hPt + 20) : 140;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(30);
  pdf.setTextColor(30, 41, 59);
  const coverPnLines = pdf.splitTextToSize(projectName || 'Project', CONTENT_W);
  for (const line of coverPnLines) { pdf.text(line, MARGIN, coverY); coverY += 36; }

  coverY += 8;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(15);
  pdf.setTextColor(100, 116, 139);
  pdf.text('Activity Export', MARGIN, coverY);
  coverY += 24;

  pdf.setFontSize(10);
  pdf.setTextColor(148, 163, 184);
  pdf.text(`Generated: ${gen}`, MARGIN, coverY); coverY += 18;
  if (filterSummary) { pdf.text(`Included: ${filterSummary}`, MARGIN, coverY); coverY += 18; }
  pdf.text(`${posts.length} post${posts.length !== 1 ? 's' : ''}`, MARGIN, coverY); coverY += 36;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(148, 163, 184);
  pdf.text('Generated by CrewChain', MARGIN, PT_H - 26);

  // ── Content pages ────────────────────────────────────────────────────────────
  pdf.addPage();
  y = MARGIN;

  let postIndex = 0;

  for (const post of posts) {
    postIndex += 1;
    addPageIfNeeded(100);

    const metaParts = [];
    if (co.includePostDateTime && post.timeLabel) metaParts.push(post.timeLabel);
    if (co.includeTeamName && post.teamName) metaParts.push(post.teamName);
    if (co.includeAuthorName && post.authorName) metaParts.push(post.authorName);
    const metaSuffix = metaParts.length ? ` · ${metaParts.join(' · ')}` : '';

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
    const headerLines = pdf.splitTextToSize(`Post ${postIndex}${metaSuffix}`, CONTENT_W);
    for (const line of headerLines) { addPageIfNeeded(14); pdf.text(line, MARGIN, y); y += 13; }
    y += 4;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);

    if (co.includePostBody && post.body) {
      const bodyLines = pdf.splitTextToSize(post.body, CONTENT_W);
      for (const line of bodyLines) { addPageIfNeeded(14); pdf.text(line, MARGIN, y); y += 12; }
    }
    if (co.includeMediaNotes && post.voiceLine) {
      const vl = pdf.splitTextToSize(post.voiceLine, CONTENT_W);
      for (const line of vl) { addPageIfNeeded(12); pdf.text(line, MARGIN, y); y += 11; }
    }
    if (co.includeMediaNotes && post.videoLine) {
      const vl = pdf.splitTextToSize(post.videoLine, CONTENT_W);
      for (const line of vl) { addPageIfNeeded(12); pdf.text(line, MARGIN, y); y += 11; }
    }

    // 2-up image grid
    const urls = post.imageUrls || [];
    if (urls.length > 0) addPageIfNeeded(160);
    for (let imgIdx = 0; imgIdx < urls.length; imgIdx += 2) {
      const isDouble = imgIdx + 1 < urls.length;
      if (isDouble) {
        const [raw1, raw2] = await Promise.all([
          loadImageForPdf(urls[imgIdx]),
          loadImageForPdf(urls[imgIdx + 1]),
        ]);
        const img1 = raw1 ? fitImageDrawSize(raw1, GRID_IMG_BOX) : null;
        const img2 = raw2 ? fitImageDrawSize(raw2, GRID_IMG_BOX) : null;
        const rowH = Math.max(img1?.hPt ?? 0, img2?.hPt ?? 0);
        if (rowH > 0) {
          if (y + rowH + 12 > pageH - BOTTOM) addPage();
          if (img1) { try { pdf.addImage(img1.dataUrl, 'JPEG', MARGIN, y, img1.wPt, img1.hPt, undefined, 'FAST'); } catch { /* */ } }
          if (img2) { try { pdf.addImage(img2.dataUrl, 'JPEG', MARGIN + GRID_COL_W + 8, y, img2.wPt, img2.hPt, undefined, 'FAST'); } catch { /* */ } }
          y += rowH + 8;
        } else {
          addPageIfNeeded(12);
          pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
          pdf.text('[Images could not be embedded]', MARGIN, y);
          pdf.setTextColor(0, 0, 0); pdf.setFontSize(10); y += 12;
        }
      } else {
        const raw = await loadImageForPdf(urls[imgIdx]);
        const loaded = raw ? fitImageDrawSize(raw, PDF_PRIMARY_PHOTO_BOX) : null;
        if (!loaded) {
          addPageIfNeeded(12);
          pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
          pdf.text('[Image could not be embedded]', MARGIN, y);
          pdf.setTextColor(0, 0, 0); pdf.setFontSize(10); y += 12;
          continue;
        }
        if (y + loaded.hPt + 12 > pageH - BOTTOM) addPage();
        try {
          pdf.addImage(loaded.dataUrl, 'JPEG', MARGIN, y, loaded.wPt, loaded.hPt, undefined, 'FAST');
          y += loaded.hPt + 8;
        } catch {
          addPageIfNeeded(12);
          pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
          pdf.text('[Image could not be embedded]', MARGIN, y);
          pdf.setTextColor(0, 0, 0); pdf.setFontSize(10); y += 12;
        }
      }
    }

    if (co.includeCommentThread && post.thread && post.thread.length) {
      addPageIfNeeded(28);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text('Comments', MARGIN, y);
      y += 14;
      for (const row of post.thread) {
        const indent = row.depth > 0 ? 16 : 0;
        const lineW = CONTENT_W - indent;
        const prefix = row.depth > 0 ? 'Reply' : 'Comment';
        const label = co.includeCommentAuthorNames && row.name
          ? `${prefix} — ${row.name} · ${row.timeLabel}`
          : `${prefix} · ${row.timeLabel}`;
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        const labLines = pdf.splitTextToSize(label, lineW);
        for (const line of labLines) { addPageIfNeeded(12); pdf.text(line, MARGIN + indent, y); y += 11; }
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        if (row.text) {
          const tl = pdf.splitTextToSize(String(row.text), lineW);
          for (const line of tl) { addPageIfNeeded(12); pdf.text(line, MARGIN + indent, y); y += 11; }
        }
        if (co.includeMediaNotes && row.voiceLine) {
          const vl = pdf.splitTextToSize(row.voiceLine, lineW);
          pdf.setFontSize(8);
          for (const line of vl) { addPageIfNeeded(11); pdf.text(line, MARGIN + indent, y); y += 10; }
          pdf.setFontSize(10);
        }
        if (co.includeCommentImages) {
          for (const cu of row.imageUrls || []) {
            const raw = await loadImageForPdf(cu);
            const cImg = raw ? fitImageDrawSize(raw, PDF_INLINE_PHOTO_BOX) : null;
            if (cImg && cImg.wPt > 0) {
              if (y + cImg.hPt > pageH - BOTTOM) addPage();
              try { pdf.addImage(cImg.dataUrl, 'JPEG', MARGIN + indent, y, cImg.wPt, cImg.hPt); y += cImg.hPt + 6; } catch { /* */ }
            }
          }
        }
        y += 4;
      }
    }

    // Accent separator between posts
    y += 8;
    addPageIfNeeded(20);
    pdf.setDrawColor(51, 65, 85);
    pdf.setLineWidth(0.75);
    pdf.line(MARGIN, y, MARGIN + CONTENT_W, y);
    pdf.setLineWidth(0.2);
    y += 16;
  }

  stampFooter(pdf, pdf.getNumberOfPages(), projectName, { startPage: 2, pageOffset: 1 });

  const safe = (projectName || 'project').replace(/[^a-z0-9]/gi, '-');
  const d = new Date();
  const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  pdf.save(`activity-export-${safe}-${stamp}.pdf`);
}

/**
 * Sections-based report: groups of posts with PM annotation headers.
 * Renders a cover page first, then section content with running footers.
 * @param {object} options
 * @param {string} options.projectName
 * @param {Array<{ name: string, annotation: string, posts: Array }>} options.sections
 * @param {string} [options.companyLogoUrl]
 * @param {object} [options.contentOptions]
 */
export async function buildActivitySectionsReportPdf({
  projectName,
  sections,
  companyLogoUrl = null,
  contentOptions = {},
}) {
  const co = {
    includePostDateTime: true,
    includeTeamName: true,
    includeAuthorName: true,
    includePostBody: true,
    includeMediaNotes: true,
    includeCommentThread: true,
    includeCommentAuthorNames: true,
    includeCommentImages: true,
    ...contentOptions,
  };

  const logo = companyLogoUrl ? await loadLogoDataUrlForPdf(companyLogoUrl) : null;
  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageH = PT_H;
  let y = MARGIN;

  const addPage = () => { pdf.addPage(); y = MARGIN; };
  const addPageIfNeeded = (needPt) => { if (y + needPt > pageH - BOTTOM) addPage(); };

  const gen = new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

  // ── Cover page (page 1) ──────────────────────────────────────────────────────
  if (logo) {
    const logoX = 595 - MARGIN - logo.wPt;
    try { pdf.addImage(logo.dataUrl, logo.format, logoX, 20, logo.wPt, logo.hPt, undefined, 'FAST'); } catch { /* */ }
  }

  let coverY = logo ? Math.max(140, 28 + logo.hPt + 20) : 140;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(30);
  pdf.setTextColor(30, 41, 59);
  const coverPnLines = pdf.splitTextToSize(projectName || 'Project', CONTENT_W);
  for (const line of coverPnLines) { pdf.text(line, MARGIN, coverY); coverY += 36; }

  coverY += 8;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(15);
  pdf.setTextColor(100, 116, 139);
  pdf.text('Activity Report', MARGIN, coverY);
  coverY += 24;

  pdf.setFontSize(10);
  pdf.setTextColor(148, 163, 184);
  pdf.text(`Generated: ${gen}`, MARGIN, coverY);
  coverY += 40;

  pdf.setDrawColor(226, 232, 240);
  pdf.setLineWidth(0.5);
  pdf.line(MARGIN, coverY, MARGIN + CONTENT_W, coverY);
  coverY += 20;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(148, 163, 184);
  pdf.text('CONTENTS', MARGIN, coverY);
  coverY += 14;

  for (const [i, sec] of sections.entries()) {
    const cnt = (sec.posts || []).length;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(30, 41, 59);
    pdf.text(`${i + 1}.  ${sec.name}`, MARGIN + 4, coverY);
    pdf.setFontSize(9);
    pdf.setTextColor(148, 163, 184);
    pdf.text(`${cnt} post${cnt !== 1 ? 's' : ''}`, MARGIN + CONTENT_W, coverY, { align: 'right' });
    coverY += 16;
    pdf.setDrawColor(241, 245, 249);
    pdf.setLineWidth(0.3);
    pdf.line(MARGIN + 4, coverY - 2, MARGIN + CONTENT_W, coverY - 2);
  }

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(148, 163, 184);
  pdf.text('Generated by CrewChain', MARGIN, PT_H - 26);

  // ── Content pages (page 2+) ──────────────────────────────────────────────────
  pdf.addPage();
  y = MARGIN;

  for (const [sIdx, section] of sections.entries()) {
    addPageIfNeeded(60);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(30, 41, 59);
    const secLines = pdf.splitTextToSize(`Section ${sIdx + 1}: ${section.name}`, CONTENT_W);
    for (const line of secLines) { pdf.text(line, MARGIN, y); y += 17; }
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(10);

    if (section.annotation) {
      const annLines = pdf.splitTextToSize(section.annotation, CONTENT_W - 26);
      const boxH = annLines.length * 13 + 18;
      addPageIfNeeded(boxH + 8);
      pdf.setFillColor(244, 246, 250);
      pdf.rect(MARGIN, y, CONTENT_W, boxH, 'F');
      pdf.setFillColor(30, 58, 95);
      pdf.rect(MARGIN, y, 3, boxH, 'F');
      pdf.setFont('helvetica', 'italic');
      pdf.setFontSize(10);
      pdf.setTextColor(51, 65, 85);
      y += 11;
      for (const line of annLines) { pdf.text(line, MARGIN + 14, y); y += 13; }
      y += 7;
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      y += 20;
    } else {
      y += 8;
    }

    let postIndex = 0;
    for (const post of section.posts || []) {
      postIndex += 1;
      addPageIfNeeded(100);

      const metaParts = [];
      if (co.includePostDateTime && post.timeLabel) metaParts.push(post.timeLabel);
      if (co.includeTeamName && post.teamName) metaParts.push(post.teamName);
      if (co.includeAuthorName && post.authorName) metaParts.push(post.authorName);
      const metaSuffix = metaParts.length ? ` · ${metaParts.join(' · ')}` : '';

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);
      const headerLines = pdf.splitTextToSize(`Post ${postIndex}${metaSuffix}`, CONTENT_W);
      for (const line of headerLines) { addPageIfNeeded(14); pdf.text(line, MARGIN, y); y += 13; }
      y += 4;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);

      if (co.includePostBody && post.body) {
        const bodyLines = pdf.splitTextToSize(post.body, CONTENT_W);
        for (const line of bodyLines) { addPageIfNeeded(14); pdf.text(line, MARGIN, y); y += 12; }
      }
      if (co.includeMediaNotes && post.voiceLine) {
        const vl = pdf.splitTextToSize(post.voiceLine, CONTENT_W);
        for (const line of vl) { addPageIfNeeded(12); pdf.text(line, MARGIN, y); y += 11; }
      }
      if (co.includeMediaNotes && post.videoLine) {
        const vl = pdf.splitTextToSize(post.videoLine, CONTENT_W);
        for (const line of vl) { addPageIfNeeded(12); pdf.text(line, MARGIN, y); y += 11; }
      }

      // 2-up image grid
      const urls = post.imageUrls || [];
      if (urls.length > 0) addPageIfNeeded(160);
      for (let imgIdx = 0; imgIdx < urls.length; imgIdx += 2) {
        const isDouble = imgIdx + 1 < urls.length;
        if (isDouble) {
          const [raw1, raw2] = await Promise.all([
            loadImageForPdf(urls[imgIdx]),
            loadImageForPdf(urls[imgIdx + 1]),
          ]);
          const img1 = raw1 ? fitImageDrawSize(raw1, GRID_IMG_BOX) : null;
          const img2 = raw2 ? fitImageDrawSize(raw2, GRID_IMG_BOX) : null;
          const rowH = Math.max(img1?.hPt ?? 0, img2?.hPt ?? 0);
          if (rowH > 0) {
            if (y + rowH + 12 > pageH - BOTTOM) addPage();
            if (img1) { try { pdf.addImage(img1.dataUrl, 'JPEG', MARGIN, y, img1.wPt, img1.hPt, undefined, 'FAST'); } catch { /* */ } }
            if (img2) { try { pdf.addImage(img2.dataUrl, 'JPEG', MARGIN + GRID_COL_W + 8, y, img2.wPt, img2.hPt, undefined, 'FAST'); } catch { /* */ } }
            y += rowH + 8;
          } else {
            addPageIfNeeded(12);
            pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
            pdf.text('[Images could not be embedded]', MARGIN, y);
            pdf.setTextColor(0, 0, 0); pdf.setFontSize(10); y += 12;
          }
        } else {
          const raw = await loadImageForPdf(urls[imgIdx]);
          const loaded = raw ? fitImageDrawSize(raw, PDF_PRIMARY_PHOTO_BOX) : null;
          if (!loaded) {
            addPageIfNeeded(12);
            pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
            pdf.text('[Image could not be embedded]', MARGIN, y);
            pdf.setTextColor(0, 0, 0); pdf.setFontSize(10); y += 12;
            continue;
          }
          if (y + loaded.hPt + 12 > pageH - BOTTOM) addPage();
          try {
            pdf.addImage(loaded.dataUrl, 'JPEG', MARGIN, y, loaded.wPt, loaded.hPt, undefined, 'FAST');
            y += loaded.hPt + 8;
          } catch {
            addPageIfNeeded(12);
            pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
            pdf.text('[Image could not be embedded]', MARGIN, y);
            pdf.setTextColor(0, 0, 0); pdf.setFontSize(10); y += 12;
          }
        }
      }

      if (co.includeCommentThread && post.thread && post.thread.length) {
        addPageIfNeeded(28);
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
        pdf.text('Comments', MARGIN, y); y += 14;
        for (const row of post.thread) {
          const indent = row.depth > 0 ? 16 : 0;
          const lineW = CONTENT_W - indent;
          const prefix = row.depth > 0 ? 'Reply' : 'Comment';
          const label = co.includeCommentAuthorNames && row.name
            ? `${prefix} — ${row.name} · ${row.timeLabel}`
            : `${prefix} · ${row.timeLabel}`;
          pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
          const labLines = pdf.splitTextToSize(label, lineW);
          for (const line of labLines) { addPageIfNeeded(12); pdf.text(line, MARGIN + indent, y); y += 11; }
          pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10);
          if (row.text) {
            const tl = pdf.splitTextToSize(String(row.text), lineW);
            for (const line of tl) { addPageIfNeeded(12); pdf.text(line, MARGIN + indent, y); y += 11; }
          }
          if (co.includeMediaNotes && row.voiceLine) {
            pdf.setFontSize(8);
            const vl = pdf.splitTextToSize(row.voiceLine, lineW);
            for (const line of vl) { addPageIfNeeded(11); pdf.text(line, MARGIN + indent, y); y += 10; }
            pdf.setFontSize(10);
          }
          if (co.includeCommentImages) {
            for (const cu of row.imageUrls || []) {
              const raw = await loadImageForPdf(cu);
              const cImg = raw ? fitImageDrawSize(raw, PDF_INLINE_PHOTO_BOX) : null;
              if (cImg && cImg.wPt > 0) {
                if (y + cImg.hPt > pageH - BOTTOM) addPage();
                try { pdf.addImage(cImg.dataUrl, 'JPEG', MARGIN + indent, y, cImg.wPt, cImg.hPt); y += cImg.hPt + 6; } catch { /* */ }
              }
            }
          }
          y += 4;
        }
      }

      // Post divider (lighter within a section)
      y += 6;
      addPageIfNeeded(16);
      pdf.setDrawColor(200, 210, 220);
      pdf.setLineWidth(0.4);
      pdf.line(MARGIN + 20, y, MARGIN + CONTENT_W - 20, y);
      pdf.setLineWidth(0.2);
      y += 12;
    }

    // Section divider (stronger)
    y += 6;
    addPageIfNeeded(24);
    pdf.setDrawColor(30, 58, 95);
    pdf.setLineWidth(1);
    pdf.line(MARGIN, y, MARGIN + CONTENT_W, y);
    pdf.setLineWidth(0.2);
    y += 20;
  }

  // Footer on content pages only (skip cover = page 1); renumber from 1
  stampFooter(pdf, pdf.getNumberOfPages(), projectName, { startPage: 2, pageOffset: 1 });

  const safe = (projectName || 'project').replace(/[^a-z0-9]/gi, '-');
  const d = new Date();
  const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  pdf.save(`activity-report-${safe}-${stamp}.pdf`);
}
